import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  CheckmarkCircle02Icon,
  CloudIcon,
  ComputerIcon,
  Database01Icon,
  Folder01Icon,
  Mail01Icon,
  SecurityCheckIcon,
  Time04Icon,
  UserShield01Icon,
  WorkflowCircle06Icon,
} from '@hugeicons/core-free-icons';
import { toast } from 'sonner';

import { api, apiError } from '@/lib/api';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const STEPS = ['Iniciativa y modo', 'Destino', 'Capacidades', 'Ambientes', 'Resumen y manifiesto'];

const ENVIRONMENT_PRESETS = [
  { key: 'dev', label: 'Solo desarrollo', environments: ['dev'] },
  { key: 'dev-prd', label: 'Desarrollo y producción', environments: ['dev', 'prd'] },
  { key: 'dev-stg-prd', label: 'Desarrollo, piloto y producción', environments: ['dev', 'stg', 'prd'] },
];

const INITIAL_FORM = {
  initiative_id: '',
  entry_mode: 'new',
  deployment_target: 'aws',
  application_key: '',
  description: '',
  repository_provider: '',
  capabilities: {
    database: false,
    storage: false,
    email: false,
    corporateAuth: false,
    scheduledJobs: false,
  },
  environments: ['dev'],
};

export default function ApplicationWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [initiatives, setInitiatives] = useState([]);
  const [engines, setEngines] = useState([]);
  const [manifestResult, setManifestResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [initiativeRes, applicationRes, enginesRes] = await Promise.all([
          api.get('/initiatives'),
          api.get('/applications'),
          api.get('/deploy-engines').catch(() => ({ data: [] })),
        ]);
        const initiativeData = Array.isArray(initiativeRes.data) ? initiativeRes.data : initiativeRes.data?.items || [];
        const linked = new Set((applicationRes.data || []).map((application) => application.initiative_id));
        setInitiatives(initiativeData.filter((initiative) => !linked.has(initiative.id)));
        setEngines(Array.isArray(enginesRes.data) ? enginesRes.data : []);
      } catch (error) {
        setLoadError(apiError(error));
      }
    })();
  }, []);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const toggleCapability = (key) =>
    setForm((current) => ({
      ...current,
      capabilities: { ...current.capabilities, [key]: !current.capabilities[key] },
    }));

  const selectedInitiative = useMemo(
    () => initiatives.find((initiative) => initiative.id === form.initiative_id),
    [initiatives, form.initiative_id],
  );

  const runtimeProfile = useMemo(() => {
    if (form.environments.includes('prd') && form.environments.includes('stg')) return 'extended';
    if (form.environments.includes('prd')) return 'standard';
    return 'pilot';
  }, [form.environments]);

  const template = form.capabilities.database || form.capabilities.storage ? 'standard-app' : 'static-site';

  const manifestYaml = useMemo(
    () => buildManifest(form, selectedInitiative, runtimeProfile, template),
    [form, selectedInitiative, runtimeProfile, template],
  );

  const validateManifest = useCallback(async () => {
    if (!form.initiative_id) return;
    setValidating(true);
    try {
      const { data } = await api.post(`/applications/manifest/validate`, { manifest: manifestYaml });
      setManifestResult(data);
    } catch (error) {
      // El endpoint devuelve 422 con el cuerpo del resultado cuando es inválido.
      const data = error?.response?.data;
      if (data && typeof data === 'object' && 'valid' in data) setManifestResult(data);
      else setManifestResult({ valid: false, errors: [apiError(error)] });
    } finally {
      setValidating(false);
    }
  }, [form.initiative_id, manifestYaml]);

  // Validación de manifiesto en vivo al llegar al paso de resumen o cambiar el manifiesto.
  useEffect(() => {
    if (step === 4) validateManifest();
  }, [step, validateManifest]);

  const canAdvance = useMemo(() => {
    switch (step) {
      case 0:
        return Boolean(form.initiative_id) && /^[a-z][a-z0-9-]{2,47}$/.test(form.application_key);
      case 3:
        return form.environments.length > 0;
      default:
        return true;
    }
  }, [step, form.initiative_id, form.application_key, form.environments]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        initiative_id: form.initiative_id,
        application_key: form.application_key,
        description: form.description || null,
        deployment_target: form.deployment_target,
        entry_mode: form.entry_mode,
        repository_provider: form.repository_provider || null,
        requested_environments: form.environments,
        capability_profile: {
          needs_database: form.capabilities.database,
          needs_storage: form.capabilities.storage,
          needs_email: form.capabilities.email,
          needs_corporate_auth: form.capabilities.corporateAuth,
          needs_scheduled_jobs: form.capabilities.scheduledJobs,
        },
      };
      const { data } = await api.post('/applications', payload);
      // Persistir el manifiesto validado como aprobado (sin desplegar nada).
      if (manifestResult?.valid) {
        await api
          .post(`/applications/${data.id}/manifest/validate`, { manifest: manifestYaml, persist: true })
          .catch(() => {});
      }
      toast.success('Aplicación registrada', {
        description:
          'Se generó su via-app.yaml. No se desplegó infraestructura: los motores están en modo no disponible.',
      });
      navigate(`/aplicaciones-ia/${data.id}`);
    } catch (error) {
      toast.error('No fue posible registrar la aplicación', { description: apiError(error) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-8 px-6 py-10">
      <div>
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
          <HugeiconsIcon icon={WorkflowCircle06Icon} size={15} aria-hidden="true" />
          Alta asistida
        </div>
        <h1 className="text-2xl font-extrabold tracking-tight md:text-3xl">Nueva aplicación</h1>
        <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground">
          VIA traduce decisiones de negocio en configuración técnica. Describe qué hará tu aplicación y VIA prepara lo
          necesario.
        </p>
      </div>

      <Stepper step={step} />

      {loadError && (
        <Alert variant="destructive">
          <AlertTitle>No fue posible cargar los datos</AlertTitle>
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      )}

      <Card className="shadow-none">
        <CardContent className="pt-6">
          {step === 0 && (
            <StepInitiative
              form={form}
              initiatives={initiatives}
              selectedInitiative={selectedInitiative}
              onSelectInitiative={(id) => {
                const initiative = initiatives.find((item) => item.id === id);
                setForm((current) => ({
                  ...current,
                  initiative_id: id,
                  application_key: current.application_key || slugify(initiative?.name || initiative?.title || ''),
                  description: current.description || initiative?.description || '',
                }));
              }}
              onUpdate={update}
            />
          )}
          {step === 1 && <StepTarget form={form} onUpdate={update} engines={engines} />}
          {step === 2 && <StepCapabilities form={form} onToggle={toggleCapability} />}
          {step === 3 && <StepEnvironments form={form} onUpdate={update} />}
          {step === 4 && (
            <StepSummary
              form={form}
              manifestYaml={manifestYaml}
              manifestResult={manifestResult}
              validating={validating}
              runtimeProfile={runtimeProfile}
              template={template}
            />
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <Button variant="ghost" asChild>
          <Link to="/aplicaciones-ia">Cancelar</Link>
        </Button>
        <div className="flex gap-2">
          {step > 0 && (
            <Button variant="outline" onClick={() => setStep((current) => current - 1)}>
              <HugeiconsIcon icon={ArrowLeft01Icon} size={16} data-icon="inline-start" aria-hidden="true" />
              Atrás
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button disabled={!canAdvance} onClick={() => setStep((current) => current + 1)}>
              Siguiente
              <HugeiconsIcon icon={ArrowRight01Icon} size={16} data-icon="inline-end" aria-hidden="true" />
            </Button>
          ) : (
            <Button disabled={submitting || !manifestResult?.valid} onClick={submit}>
              {submitting ? 'Registrando…' : 'Registrar aplicación'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stepper({ step }) {
  return (
    <ol className="flex flex-wrap gap-2">
      {STEPS.map((label, index) => {
        const state = index === step ? 'current' : index < step ? 'done' : 'todo';
        return (
          <li
            key={label}
            className={
              'flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium ' +
              (state === 'current'
                ? 'border-primary bg-primary/10 text-primary'
                : state === 'done'
                  ? 'border-primary/40 text-primary'
                  : 'border-border text-muted-foreground')
            }>
            <span className="flex size-5 items-center justify-center rounded-full bg-primary/15 text-[11px] font-bold text-primary">
              {state === 'done' ? (
                <HugeiconsIcon icon={CheckmarkCircle02Icon} size={13} aria-hidden="true" />
              ) : (
                index + 1
              )}
            </span>
            {label}
          </li>
        );
      })}
    </ol>
  );
}

function StepInitiative({ form, initiatives, selectedInitiative, onSelectInitiative, onUpdate }) {
  return (
    <div className="space-y-5">
      <Field
        label="Iniciativa VIA"
        htmlFor="wizard-initiative"
        hint="El alta exige una iniciativa inscrita y evaluada en el Registro.">
        <Select value={form.initiative_id} onValueChange={onSelectInitiative}>
          <SelectTrigger id="wizard-initiative">
            <SelectValue placeholder="Selecciona una iniciativa" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {initiatives.map((initiative) => (
                <SelectItem key={initiative.id} value={initiative.id}>
                  {initiative.code ? `${initiative.code} · ` : ''}
                  {initiative.name || initiative.title}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </Field>
      {!initiatives.length && (
        <Alert>
          <AlertTitle>No hay iniciativas disponibles</AlertTitle>
          <AlertDescription>
            Inscribe una iniciativa en{' '}
            <Link to="/registro" className="font-semibold underline">
              AI Registry
            </Link>{' '}
            antes de crear una aplicación.
          </AlertDescription>
        </Alert>
      )}
      {selectedInitiative && (
        <Alert>
          <AlertTitle>{selectedInitiative.name || selectedInitiative.title}</AlertTitle>
          <AlertDescription>
            Estado de evaluación: {selectedInitiative.evaluation_status || 'Pendiente'}. La base de datos permanece
            bloqueada hasta completar la evaluación.
          </AlertDescription>
        </Alert>
      )}

      <Field label="¿Cómo entra la aplicación?">
        <RadioGroup
          value={form.entry_mode}
          onValueChange={(value) => onUpdate('entry_mode', value)}
          className="grid gap-3 sm:grid-cols-3">
          <ModeCard
            value="new"
            title="Crear nueva"
            description="Repositorio, workspace y despliegue asistido."
            current={form.entry_mode}
          />
          <ModeCard
            value="existing"
            title="Registrar existente"
            description="Solo inventario y gobierno, sin aprovisionar."
            current={form.entry_mode}
          />
          <ModeCard
            value="adopt"
            title="Adoptar autoservicio"
            description="Existente que luego adopta el motor de despliegue."
            current={form.entry_mode}
          />
        </RadioGroup>
      </Field>

      <Field
        label="Identificador técnico"
        htmlFor="wizard-app-key"
        hint="Minúsculas, números y guiones. Entre 3 y 48 caracteres.">
        <Input
          id="wizard-app-key"
          required
          pattern="[a-z][a-z0-9-]{2,47}"
          placeholder="control-capacitaciones"
          value={form.application_key}
          onChange={(event) => onUpdate('application_key', event.target.value.toLowerCase())}
        />
      </Field>
    </div>
  );
}

function StepTarget({ form, onUpdate, engines }) {
  const engineFor = (key) => engines.find((engine) => engine.key === key);
  const selectedEngine = engineFor(form.deployment_target);
  const targets = [
    {
      value: 'aws',
      icon: CloudIcon,
      title: 'AWS (nube)',
      description: 'VIA orquesta el despliegue en la nube. Ideal para aplicaciones accesibles desde internet.',
    },
    {
      value: 'on-premise',
      icon: ComputerIcon,
      title: 'On-premise (interno)',
      description:
        'Se ejecuta en la red corporativa desde tu equipo con ayuda de la IA. VIA prepara y gobierna, pero no se conecta al servidor interno.',
    },
  ];
  return (
    <div className="space-y-5">
      <RadioGroup
        value={form.deployment_target}
        onValueChange={(value) => onUpdate('deployment_target', value)}
        className="grid gap-3 sm:grid-cols-2">
        {targets.map((target) => {
          const engine = engineFor(target.value);
          return (
            <label
              key={target.value}
              className={
                'flex cursor-pointer flex-col gap-2 rounded-xl border p-4 transition ' +
                (form.deployment_target === target.value
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/40')
              }>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold">
                  <HugeiconsIcon icon={target.icon} size={20} className="text-primary" aria-hidden="true" />
                  {target.title}
                </span>
                <RadioGroupItem value={target.value} />
              </div>
              <p className="text-sm text-muted-foreground">{target.description}</p>
              {engine && (
                <Badge variant={engine.available ? 'default' : 'secondary'} className="w-fit">
                  {engine.available ? 'Motor disponible' : 'Motor no disponible'}
                </Badge>
              )}
            </label>
          );
        })}
      </RadioGroup>
      <Alert>
        <HugeiconsIcon icon={SecurityCheckIcon} aria-hidden="true" />
        {selectedEngine?.available ? (
          <>
            <AlertTitle>El alta no despliega nada por sí sola</AlertTitle>
            <AlertDescription>
              {selectedEngine.message ||
                'El motor está disponible. El despliegue se prepara y ejecuta de forma controlada después del alta.'}
            </AlertDescription>
          </>
        ) : (
          <>
            <AlertTitle>Este asistente no despliega nada</AlertTitle>
            <AlertDescription>
              {selectedEngine?.message ||
                'El motor de despliegue de este destino no está disponible en este entorno. El alta registra y gobierna la aplicación; el despliegue se habilita más adelante.'}
            </AlertDescription>
          </>
        )}
      </Alert>
    </div>
  );
}

function StepCapabilities({ form, onToggle }) {
  const capabilities = [
    {
      key: 'database',
      icon: Database01Icon,
      title: 'Guarda datos',
      description: 'Base de datos gestionada por VIA.',
    },
    {
      key: 'storage',
      icon: Folder01Icon,
      title: 'Guarda archivos',
      description: 'Almacenamiento aislado por ambiente.',
    },
    { key: 'email', icon: Mail01Icon, title: 'Envía correo', description: 'Envío autorizado según el destino.' },
    {
      key: 'corporateAuth',
      icon: UserShield01Icon,
      title: 'Autenticación corporativa',
      description: 'Inicio de sesión con EntraID.',
    },
    { key: 'scheduledJobs', icon: Time04Icon, title: 'Tareas programadas', description: 'Procesos sin interfaz.' },
  ];
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        ¿Qué hará la aplicación? VIA entrega exactamente los recursos que declares.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {capabilities.map((capability) => (
          <label
            key={capability.key}
            className={
              'flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ' +
              (form.capabilities[capability.key]
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/40')
            }>
            <Checkbox
              checked={form.capabilities[capability.key]}
              onCheckedChange={() => onToggle(capability.key)}
              className="mt-0.5"
            />
            <div>
              <span className="flex items-center gap-2 font-semibold">
                <HugeiconsIcon icon={capability.icon} size={18} className="text-primary" aria-hidden="true" />
                {capability.title}
              </span>
              <p className="text-sm text-muted-foreground">{capability.description}</p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function StepEnvironments({ form, onUpdate }) {
  const activePreset = ENVIRONMENT_PRESETS.find(
    (preset) =>
      preset.environments.length === form.environments.length &&
      preset.environments.every((env) => form.environments.includes(env)),
  );
  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Elige los ambientes de la aplicación. No es obligatorio tener los tres.
      </p>
      <RadioGroup
        value={activePreset?.key || 'custom'}
        onValueChange={(key) => {
          const preset = ENVIRONMENT_PRESETS.find((item) => item.key === key);
          if (preset) onUpdate('environments', preset.environments);
        }}
        className="grid gap-3 sm:grid-cols-3">
        {ENVIRONMENT_PRESETS.map((preset) => (
          <label
            key={preset.key}
            className={
              'flex cursor-pointer flex-col gap-2 rounded-xl border p-4 transition ' +
              (activePreset?.key === preset.key
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/40')
            }>
            <div className="flex items-center justify-between">
              <span className="font-semibold">{preset.label}</span>
              <RadioGroupItem value={preset.key} />
            </div>
            <div className="flex gap-1.5">
              {preset.environments.map((env) => (
                <Badge key={env} variant="secondary">
                  {env.toUpperCase()}
                </Badge>
              ))}
            </div>
          </label>
        ))}
      </RadioGroup>
      <Field label="Ajuste manual">
        <div className="flex flex-wrap gap-3">
          {['dev', 'stg', 'prd'].map((env) => {
            const checked = form.environments.includes(env);
            return (
              <label key={env} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => {
                    const next = checked
                      ? form.environments.filter((item) => item !== env)
                      : [...form.environments, env];
                    onUpdate(
                      'environments',
                      ['dev', 'stg', 'prd'].filter((item) => next.includes(item)),
                    );
                  }}
                />
                {env.toUpperCase()}
              </label>
            );
          })}
        </div>
      </Field>
    </div>
  );
}

function StepSummary({ form, manifestYaml, manifestResult, validating, runtimeProfile, template }) {
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <SummaryRow label="Destino" value={form.deployment_target === 'aws' ? 'AWS (nube)' : 'On-premise (interno)'} />
        <SummaryRow label="Modo de entrada" value={form.entry_mode} />
        <SummaryRow label="Plantilla" value={template} />
        <SummaryRow label="Perfil de ejecución" value={runtimeProfile} />
        <SummaryRow label="Ambientes" value={form.environments.map((env) => env.toUpperCase()).join(', ')} />
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label>Manifiesto generado (via-app.yaml)</Label>
          {validating ? (
            <Badge variant="secondary">Validando…</Badge>
          ) : manifestResult?.valid ? (
            <Badge>Válido</Badge>
          ) : manifestResult ? (
            <Badge variant="destructive">Con errores</Badge>
          ) : null}
        </div>
        <pre className="max-h-72 overflow-auto rounded-xl border bg-muted/40 p-4 text-xs leading-relaxed">
          {manifestYaml}
        </pre>
      </div>

      {manifestResult && !manifestResult.valid && (
        <Alert variant="destructive">
          <AlertTitle>El manifiesto tiene observaciones</AlertTitle>
          <AlertDescription>
            <ul className="ml-4 list-disc space-y-1">
              {(manifestResult.errors || []).map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      {manifestResult?.valid && (
        <Alert>
          <HugeiconsIcon icon={CheckmarkCircle02Icon} aria-hidden="true" />
          <AlertTitle>Manifiesto válido</AlertTitle>
          <AlertDescription>
            Al registrar, VIA guarda este manifiesto. No se desplegará infraestructura en esta versión.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

function ModeCard({ value, title, description, current }) {
  return (
    <label
      className={
        'flex cursor-pointer flex-col gap-2 rounded-xl border p-4 transition ' +
        (current === value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40')
      }>
      <div className="flex items-center justify-between">
        <span className="font-semibold">{title}</span>
        <RadioGroupItem value={value} />
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </label>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="rounded-lg border bg-muted/30 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-semibold capitalize">{value}</p>
    </div>
  );
}

function Field({ label, hint, htmlFor, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function slugify(value) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
}

function buildManifest(form, initiative, runtimeProfile, template) {
  const backendEnabled = form.capabilities.database || template === 'standard-app';
  const environments = form.environments.length ? form.environments : ['dev'];
  const lines = [
    'apiVersion: via.ucchristus/v1alpha1',
    'kind: Application',
    'metadata:',
    `  name: ${form.application_key || 'aplicacion'}`,
    `  initiativeId: ${initiative?.code || initiative?.id || 'VIA-000000'}`,
    `  owner: ${initiative?.owner || 'equipo'}`,
    'spec:',
    `  deploymentTarget: ${form.deployment_target}`,
    `  template: ${template}`,
    `  runtimeProfile: ${runtimeProfile}`,
    `  environments: [${environments.join(', ')}]`,
    '  components:',
    '    frontend: { enabled: true }',
    backendEnabled ? '    backend: { enabled: true, healthcheck: /api/health }' : '    backend: { enabled: false }',
    '  capabilities:',
    `    database: ${form.capabilities.database}`,
    `    storage: ${form.capabilities.storage}`,
    `    email: ${form.capabilities.email}`,
    `    corporateAuth: ${form.capabilities.corporateAuth}`,
    `    scheduledJobs: ${form.capabilities.scheduledJobs}`,
    '  security:',
    '    dataClassification: internal',
    '    internetEgress: false',
    '  value:',
    '    metric: horas-ahorradas-mensuales',
    '    target: 40',
  ];
  return lines.join('\n') + '\n';
}
